import * as React from "react";
import { Brand, Button, useAuth } from "@ozpanel/ui";

// The phone's side of "set up on your phone" (auth/pairing.go): the QR opens <base>/#pair?token=<code>.
// This screen spends the code ONCE, signs the phone in as a device of its own, and hands over to the
// Notifications page — the one that already walks THIS device through installing the app and turning on
// new-mail alerts (InstallAndNotify), so the setup continues there instead of in a second copy of it.
//
// Handled before the sign-in gate, not by LoginScreen like a reset link: a phone already signed in as
// another mailbox must still be able to use the code, and a signed-in phone never sees LoginScreen.

/** The code from a pairing link, or null. Read from the fragment, which never reaches a server. */
export function readPairLink(loc: { hash: string } = window.location): string | null {
  const raw = loc.hash.replace(/^#\/?/, "");
  if (!raw.startsWith("pair")) return null;
  const q = raw.indexOf("?");
  const token = new URLSearchParams(q >= 0 ? raw.slice(q + 1) : "").get("token");
  return token && /^[0-9a-f]{16,128}$/i.test(token) ? token : null;
}

export function PairScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const { client, loading, refresh } = useAuth();
  const [error, setError] = React.useState<string | null>(null);
  const spent = React.useRef(false);

  React.useEffect(() => {
    // After the provider's own first session check: a late answer from it must not overwrite the session
    // this code is about to create. Once only — a remount (StrictMode, a re-render) must not spend twice.
    if (loading || spent.current) return;
    spent.current = true;
    // The code leaves the address bar at once: a spent credential in history, a screenshot or a
    // bookmark helps nobody.
    window.history.replaceState(null, "", window.location.pathname);
    void (async () => {
      try {
        await client.apiFetch("/auth/pair/redeem", { method: "POST", body: JSON.stringify({ token }) });
        await refresh();
        // Straight to the device's own setup: install, then alerts. The router is built after this
        // (MailboxShell builds it on first render), so it starts at this address.
        window.history.replaceState(null, "", import.meta.env.BASE_URL.replace(/\/+$/, "") + "/notifications?setup=1");
        onDone();
      } catch (e) {
        setError(e instanceof Error && e.message ? e.message : "This code could not be used.");
      }
    })();
  }, [loading, client, refresh, token, onDone]);

  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className="w-full max-w-[390px] text-center">
        <div className="mb-6 flex justify-center">
          <Brand />
        </div>
        {!error ? (
          <p className="text-[14px] text-muted">Signing this phone in…</p>
        ) : (
          <div className="space-y-4">
            <h1 className="text-[18px] font-semibold">This code did not work</h1>
            <p className="text-[13.5px] leading-relaxed text-muted">{error}</p>
            <p className="text-[13px] text-muted">
              Ask for a new code on the screen that showed it, or sign in with your email and password.
            </p>
            <Button variant="accent" onClick={onDone}>
              Sign in instead
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
